import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, Card, message, Modal, Select } from 'antd';
import { ReloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const EmailTemplates: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [validateModalVisible, setValidateModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [patchAll, setPatchAll] = useState<boolean>(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_EMAIL_TEMPLATES_GET);
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.data || (Array.isArray(data) ? data : []);
        setTemplates(items);
      }
    } catch (error: any) {
      message.error('Failed to fetch email templates');
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    try {
      setValidating(true);
      const params = new URLSearchParams();
      params.set('patchAll', patchAll.toString());
      if (selectedTemplate && !patchAll) {
        params.set('templateKey', selectedTemplate);
      }

      const response = await apiClient.post(`${API_ENDPOINTS.ADMIN_EMAIL_TEMPLATES_VALIDATE}?${params}`);
      
      if (response.data?.status === 'success') {
        message.success('Email templates validated and synced successfully');
        setValidateModalVisible(false);
        setSelectedTemplate('');
        setPatchAll(false);
        fetchTemplates();
      } else {
        message.error(response.data?.message || 'Failed to validate templates');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to validate templates');
    } finally {
      setValidating(false);
    }
  };

  const columns = [
    {
      title: 'Template Key',
      dataIndex: 'templateKey',
      key: 'templateKey',
    },
    {
      title: 'Usage Type',
      dataIndex: 'usageType',
      key: 'usageType',
    },
    {
      title: 'File ID',
      dataIndex: 'fileId',
      key: 'fileId',
      ellipsis: true,
    },
    {
      title: 'Company ID',
      dataIndex: 'companyId',
      key: 'companyId',
      ellipsis: true,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>Email Templates</Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchTemplates}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => setValidateModalVisible(true)}
          >
            Validate & Sync Templates
          </Button>
        </Space>
      </HeaderContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={templates}
            rowKey="id"
            loading={loading}
            pagination={false}
            style={{ background: 'transparent' }}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title="Validate & Sync Email Templates"
        open={validateModalVisible}
        onOk={handleValidate}
        onCancel={() => {
          setValidateModalVisible(false);
          setSelectedTemplate('');
          setPatchAll(false);
        }}
        okText="Validate & Sync"
        cancelText="Cancel"
        confirmLoading={validating}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
        style={{ top: 20 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
              Patch All Templates
            </Text>
            <Select
              style={{ width: '100%' }}
              value={patchAll ? 'true' : 'false'}
              onChange={(value) => setPatchAll(value === 'true')}
            >
              <Select.Option value="false">No - Sync specific template</Select.Option>
              <Select.Option value="true">Yes - Sync all templates</Select.Option>
            </Select>
          </div>
          {!patchAll && (
            <div>
              <Text style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
                Select Template Key (optional)
              </Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Select a template key"
                value={selectedTemplate || undefined}
                onChange={setSelectedTemplate}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={templates.map(t => ({
                  value: t.templateKey,
                  label: t.templateKey,
                }))}
              />
            </div>
          )}
        </Space>
      </Modal>
    </PageContainer>
  );
};

export default EmailTemplates;

